import React from "react";

import { ReferenceBlockModel } from "@courses-models/course";
import { ContentElementModel, ContentModel } from "@courses-pm2/api";

import { getIcon } from "@apps/viewer/src/assets/icons";
import { colors } from "@apps/viewer/src/ts/config";
import { injectStore, Store } from "@apps/viewer/src/ts/store";

import { Block } from "@apps/viewer/src/ts/components/block/Block";
import { AudioPlayer } from "@apps/viewer/src/ts/components/utils/AudioPlayer";

type ReferenceBlockProps = {
    /**
     * A reference to the store that's backing the application lifecycle state.
     */
    store: Store;

    /**
     * A reference to the model representing the block that's currently being
     * rendered.
     */
    model: ReferenceBlockModel;

    /**
     * Contains the fetched content that's part of the referenced element.
     */
    content: ContentModel;
};

/**
 * Renders content that refers to external elements (currently only elements
 * from PM2 is supported).
 */
class ReferenceBlockUnwrapped extends Block<ReferenceBlockProps> {
    /**
     * Renderer that injects the content as entered by the user himself.
     */
    protected renderContent(): React.JSX.Element {
        if (!this.props.content.isAuthorized) {
            return this.renderUnauthorized();
        }

        // Grab a reference to the portal that the content from this reference
        // is associated with, so that we can create a back-link to the portal
        const portal = this.props.store.portals.find((a) => a.id === this.props.model.product.id);

        // If a portal was actually found, then generate the link to the
        // frontpage (we need to determine if https is supported based on the
        // domain structure)
        let portalLink: string | undefined;

        if (portal && portal.primaryDomainName) {
            const protocol = (
                portal.primaryDomainName.match(/^[^\.]+\.(gyldendal|munksgaard|hansreitzel.dk)\.dk/)
                    ? "https"
                    : "http"
            );

            portalLink = `${protocol}://${portal.primaryDomainName}/`;
        }

        // Render an audio player at the top of the page, if only a single
        // element with an associated speech file has been added to the course
        const mainElement = this.props.content.elements[0];
        const speechFile = this.props.content.elements.length === 1 && mainElement instanceof ContentElementModel
            ? mainElement.speechFile
            : undefined;

        return (
            <>
                {speechFile && !mainElement.isScrambled && <AudioPlayer src={speechFile} />}

                {this.props.content.isScrambled && this.renderScrambledMessage()}

                <div className="referenceBlock">
                    {this.props.content.render()}

                    <div className="referenceBlock__creditLine">
                        {(
                            portal && portalLink
                                ? (
                                    <a
                                        href={portalLink}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {portal.title}
                                    </a>
                                )
                                : this.props.model.product.title
                        )}
                    </div>

                    <style jsx>{`
                        .referenceBlock {
                            /** Remove margins from first and last children */
                            & > :global(:first-child) {
                                margin-top: 0;
                            }

                            & > :global(:last-child) {
                                margin-bottom: 0;
                            }
                        }

                        .referenceBlock__creditLine {
                            /** Apply margins */
                            margin-top: 40px;
                            padding-top: 20px;

                            /** Render horizontal ruler */
                            border-top: 1px solid #e5e5e5;

                            /** Apply text styling */
                            color: ${ colors.black};
                            font-size: 12px;
                            font-weight: 500;

                            /** Apply link styling */
                            & a {
                                color: ${ colors.black};
                                text-decoration: none;

                                /** Prepare hover transition */
                                transition: color 0.1s linear;

                                &:hover {
                                    color: ${ colors.orange};
                                }
                            }
                        }
                    `}</style>
                </div>
            </>
        );
    }

    /**
     * Renderer that'll inject an "unauthorized" message if the user doesn't
     * have access to the referenced content.
     */
    protected renderUnauthorized(): React.JSX.Element {
        return (
            <div className="unauthorized">
                <i className="lock">
                    {getIcon("lock", { strokeColor: colors.black })}
                </i>

                <h1>Du har ikke adgang</h1>
                Det login du benytter giver ikke adgang til denne side

                <style jsx>{`
                    .unauthorized {
                        /** Center message within viewport */
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);

                        /** Set up width */
                        width: calc(100vw - 60px);

                        /** Apply text styling */
                        font-size: 18px;
                        line-height: 1.3em;
                        text-align: center;
                    }

                    .lock {
                        /** Apply sizing */
                        display: block;
                        width: 140px;
                        height: 140px;

                        /** Center horizontally */
                        margin: 0 auto;
                    }

                    h1 {
                        /** Reset default margins */
                        margin: 30px 0 10px;
                        padding: 0;

                        /** Apply text styling */
                        font-size: 30px;
                        font-weight: 500;
                        line-height: 1em;
                    }

                    @media screen and (max-width: 600px) {
                        .unauthorized {
                            /** Downscale message */
                            font-size: 16px;
                        }

                        h1 {
                            /** Downscale header */
                            font-size: 24px;
                        }

                        .lock {
                            /** Resize icon */
                            width: 100px;
                            height: 100px;
                        }
                    }
                `}</style>
            </div>
        );
    }

    /**
     * Renderer that'll inject a message regarding missing authorization in case
     * a user is seeing scrambled content.
     */
    protected renderScrambledMessage(): React.JSX.Element {
        return (
            <div className="unauthorized">
                <h1>Du har ikke adgang til dette indhold</h1>
                Du har desværre ikke adgang til denne side i forløbet. Det skyldes, at indholdet på siden ikke er en del af dit abonnement.

                <style jsx>{`
                    .unauthorized {
                        /** Center container */
                        margin: 0 auto 50px;
                        width: 490px;
                        max-width: 100%;

                        /** Apply text styling */
                        font-size: 16px;
                        line-height: 1.3em;
                        text-align: left;
                    }

                    h1 {
                        /** Reset default margins */
                        margin: 0 0 30px;
                        padding: 0;

                        /** Apply text styling */
                        font-size: 32px;
                        font-weight: 400;
                        line-height: 1em;
                    }

                    @media screen and (max-width: 600px) {
                        h1 {
                            /** Downscale header */
                            font-size: 24px;
                        }
                    }
                `}</style>
            </div>
        );
    }
}

// tslint:disable-next-line variable-name
export const ReferenceBlock = injectStore(ReferenceBlockUnwrapped);
